import {useEffect,useState} from "react";
import type {PlatformApiClient} from "../api";
import type {OrganizationContext} from "../types";

type Plan="gestion"|"operaciones"|"autopiloto";
type Offer={planKey:Plan;amountMinor:number;currency:"ARS"};
type CancellationStatus={state:"none"|"mandate_present_unverified"|"requested"|"canceled";planKey?:Plan;renewalScheduled:boolean|null;accessUntil:string|null;granted:false};
const formatUntil=(value:string)=>new Intl.DateTimeFormat("es-AR",{dateStyle:"long"}).format(new Date(value));
const plans:readonly Plan[]=["gestion","operaciones","autopiloto"];
const names:Record<Plan,string>={gestion:"Gestión",operaciones:"Operaciones",autopiloto:"Autopiloto"};
const checkoutHosts=new Set(["mercadopago.com","mercadopago.com.ar","www.mercadopago.com","www.mercadopago.com.ar","sandbox.mercadopago.com.ar"]);
const money=(v:number)=>new Intl.NumberFormat("es-AR",{style:"currency",currency:"ARS",maximumFractionDigits:2}).format(v/100);
function validCheckoutUrl(raw:unknown):string {
 if(typeof raw!=="string")throw Error("Mercado Pago no devolvió una dirección de pago válida.");
 const url=new URL(raw);
 if(url.protocol!=="https:"||!checkoutHosts.has(url.hostname)||url.username||url.password)throw Error("Destino de pago no permitido.");
 return url.href;
}
/** Commercial self-service only for the staging allowlisted owner/admin.
 * The authoritative Platform backend still rechecks membership, billing and plan.
 */
export function ContenedoresSubscribe({organization,api}:{organization:OrganizationContext;api:PlatformApiClient}){
 const [offers,setOffers]=useState<Offer[]>([]);
 const [plan,setPlan]=useState<Plan>("gestion");
 const [loading,setLoading]=useState(true);
 const [busy,setBusy]=useState(false);
 const [error,setError]=useState("");
 const [notice,setNotice]=useState("");
 const [cancellation,setCancellation]=useState<CancellationStatus|null>(null);
 const [confirmCancel,setConfirmCancel]=useState(false);
 const [statusError,setStatusError]=useState("");
 const org=organization.organization.id;
 const base="/api/v1/platform/organizations/"+org+"/contenedores/subscriptions";
 const loadCancellation=async()=>{
  const response=await fetch(base+"/status",{method:"GET",credentials:"include",cache:"no-store",redirect:"error"});
  if(!response.ok)throw Error("No se pudo consultar el estado de la suscripción.");
  const payload=await response.json() as {data?:CancellationStatus};
  const state=payload.data;
  if(!state||!["none","mandate_present_unverified","requested","canceled"].includes(state.state))
   throw Error("El servidor devolvió un estado de suscripción inesperado.");
  setCancellation(state);
  return state;
 };
 useEffect(()=>{
  let live=true;
  setLoading(true);setError("");setOffers([]);
  void fetch(base+"/offers",{method:"GET",credentials:"include",cache:"no-store",redirect:"error"})
   .then(async response=>{
    if(!response.ok)throw Error("El catálogo todavía no está habilitado para esta empresa.");
    const value=await response.json() as {data?:{plans?:Offer[]}};
    const items=value.data?.plans;
    if(!Array.isArray(items)||items.length!==3||!plans.every(k=>items.some(x=>x.planKey===k&&x.currency==="ARS"&&Number.isSafeInteger(x.amountMinor)&&x.amountMinor>0)))
      throw Error("El catálogo de planes no está disponible.");
    if(live)setOffers(items);
   })
   .catch(e=>{if(live)setError(e instanceof Error?e.message:"No se pudo cargar el catálogo.")})
   .finally(()=>{if(live)setLoading(false)});
  void loadCancellation().then(()=>{if(live)setStatusError("")}).catch(()=>{
   if(live)setStatusError("No se pudo verificar el estado de la suscripción. No se realizarán cambios sin confirmación.");
  });
  return ()=>{live=false};
 },[base]);
 const submit=async(action:"subscribe"|"reconcile"|"cancel")=>{
  if(busy||(action!=="cancel"&&(loading||offers.length!==3))||!cancellation)return;
  if(action==="subscribe"&&["canceled","requested"].includes(cancellation.state))return;
  if(action==="cancel"&&(!cancellation.planKey||!["requested","mandate_present_unverified"].includes(cancellation.state)))return;
  setBusy(true);setError("");setNotice("");
  try{
   const csrf=(await api.refreshCsrf()).csrfToken;
   if(!csrf)throw Error("Sesión caducada. Volvé a iniciar sesión.");
   const res=await fetch(base+"/"+action,{
    method:"POST",credentials:"include",cache:"no-store",redirect:"error",
    headers:{"content-type":"application/json","x-csrf-token":csrf},
    body:JSON.stringify({planKey:action==="cancel"?cancellation.planKey:plan})
   });
   const body=await res.json() as {data?:{state?:string;checkoutUrl?:string|null;granted?:boolean;accessUntil?:string|null;renewalScheduled?:boolean|null}};
   if(!res.ok)throw Error(action==="cancel"?
    "La cancelación necesita conciliación con Mercado Pago. No volveremos a enviar una segunda cancelación automáticamente.":
    "No se pudo iniciar o reconciliar la suscripción. Revisá el estado del plan.");
   if(body.data?.granted!==false)throw Error("La suscripción todavía no tiene una confirmación financiera verificada.");
   if(action==="cancel"){
    setConfirmCancel(false);
    const state=await loadCancellation();
    setNotice(state.state==="canceled"?
     (state.accessUntil?"Se cancelaron los próximos cobros. Tu acceso pagado continúa hasta el "+formatUntil(state.accessUntil)+".":"Se cancelaron los próximos cobros; no hay un período pago vigente verificado."):
     "La cancelación necesita confirmación de Mercado Pago. Todavía no podemos garantizar que los próximos cobros estén detenidos. La operación no se enviará dos veces automáticamente.");
    return;
   }
   if(body.data.checkoutUrl){
    window.location.assign(validCheckoutUrl(body.data.checkoutUrl));
    return;
   }
   setNotice("La suscripción está pendiente de confirmación de Mercado Pago. El acceso no se habilita hasta verificar el cobro.");
   await loadCancellation();
  }catch(e){setError(e instanceof Error?e.message:"No se pudo completar la solicitud.")}
  finally{setBusy(false)}
 };
 if(!organization.contenedoresCanaryEligible||!["owner","admin"].includes(organization.membership.role))return null;
 return <section className="details-card" aria-label="Suscripción a ContenedorOS">
  <h2>Activar ContenedorOS</h2>
  <p>Elegí el plan de gestión para tu empresa. El acceso se habilita después de que ClickDialog verifique el pago y cree tu espacio.</p>
  {loading?<p role="status">Consultando planes disponibles…</p>:null}
  {statusError?<p role="alert">{statusError}</p>:null}
  {cancellation?.state==="requested"?<div role="status">
    <p>Cancelación pendiente de verificación. Podés consultar nuevamente; no se repetirá la solicitud de cancelación.</p>
    <button type="button" disabled={busy} onClick={()=>void submit("cancel")}>Verificar cancelación</button>
  </div>:null}
  {cancellation?.state==="canceled"?<div role="status">
    <p>Próximos cobros cancelados. {cancellation.accessUntil?"El acceso pagado continúa hasta el "+formatUntil(cancellation.accessUntil)+".":"No se ha verificado un período de acceso pago vigente."}</p>
  </div>:null}
  {offers.length===3&&cancellation&&!["requested","canceled"].includes(cancellation.state)?<div>
    <label htmlFor="ct-subscription-plan">Plan</label>{" "}
    <select id="ct-subscription-plan" value={plan} disabled={busy} onChange={e=>setPlan(e.target.value as Plan)}>
      {plans.map(k=><option key={k} value={k}>{names[k]} — {money(offers.find(x=>x.planKey===k)!.amountMinor)}/mes</option>)}
    </select>
    <p>Los pagos son gestionados por Mercado Pago. No se activa el plan por volver desde el checkout.</p>
    <button type="button" disabled={busy} onClick={()=>void submit("subscribe")}>Suscribirme</button>{" "}
    <button type="button" disabled={busy} onClick={()=>void submit("reconcile")}>Revisar pago pendiente</button>
    {cancellation.state==="mandate_present_unverified"?<div>
      <p>Se detectó una solicitud de suscripción. Si no querés más renovaciones, podés cancelarla. El acceso se mantiene hasta finalizar cualquier período pago verificado.</p>
      {!confirmCancel?<button type="button" disabled={busy} onClick={()=>setConfirmCancel(true)}>Cancelar próximos cobros</button>:
       <div role="group" aria-label="Confirmar cancelación">
        <p>¿Confirmás cancelar los próximos cobros de {names[cancellation.planKey??plan]}? No se realizará ningún reembolso automático.</p>
        <button type="button" disabled={busy} onClick={()=>void submit("cancel")}>Sí, cancelar renovación</button>{" "}
        <button type="button" disabled={busy} onClick={()=>setConfirmCancel(false)}>Volver</button>
       </div>}
    </div>:null}
  </div>:null}
  {error?<p role="alert">{error}</p>:null}
  {notice?<p role="status">{notice}</p>:null}
 </section>;
}
